import { unique } from 'remeda';
import { v5 as uuidV5 } from 'uuid';
import type { UUID } from '../../types/common';
import { assertNever } from '../../utilities/assertNever';
import type {
  AddAttachmentEvent,
  AddGeotagEvent,
  AddNoteEvent,
  BuiltList,
  CopyEvent,
  CreateListEvent,
  EditGeotagEvent,
  EditNoteEvent,
  ExpandProtoEvent,
  Field,
  FileAttachment,
  GeotagAttachment,
  InsertEvent,
  ListEvent,
  ModifyListEvent,
  MoveEvent,
  NoteAttachment,
  PatchEvent,
  RemoveAttachmentEvent,
  RemoveEvent,
  RemoveGeotagEvent,
  RemoveNoteEvent,
  RootField,
  StructureField,
  ValueField,
} from './Checklist.types';
import { completePartialField, createRootField } from './Checklist.types';

// ---------------------------------------------------------------------------
// Tree utilities
// ---------------------------------------------------------------------------

/**
 * Find a node in the tree by id. O(n) traversal - no index maintained.
 */
export function findNode(tree: RootField, id: UUID): Field | undefined {
  if (tree.id === id) return tree;
  return findNodeInChildren(tree, id);
}

function findNodeInChildren(node: StructureField, id: UUID): Field | undefined {
  for (const child of node.children) {
    if (child.id === id) return child;
    if (child.type === 'GROUP_FIELD' || child.type === 'ROOT_FIELD') {
      const found = findNodeInChildren(child, id);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Find a node and collect the path from the root to that node (inclusive).
 * Returns undefined if the node is not found.
 */
export function findNodePath(tree: RootField, id: UUID): Field[] | undefined {
  if (tree.id === id) return [tree];
  return findPathInChildren(tree, id, [tree]);
}

function findPathInChildren(
  node: StructureField,
  id: UUID,
  pathSoFar: Field[],
): Field[] | undefined {
  for (const child of node.children) {
    if (child.id === id) return [...pathSoFar, child];
    if (child.type === 'GROUP_FIELD' || child.type === 'ROOT_FIELD') {
      const found = findPathInChildren(child, id, [...pathSoFar, child]);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Recursively collect all descendant nodes, including the node itself.
 */
export function getAllDescendants(node: Field): Field[] {
  if (node.type === 'ROOT_FIELD' || node.type === 'GROUP_FIELD') {
    return [node, ...node.children.flatMap(getAllDescendants)];
  }
  return [node];
}

/**
 * Collect all leaf-level (value) fields from the tree in document order.
 * Skips ROOT_FIELD and GROUP_FIELD nodes. Useful for prev/next navigation.
 */
export function getLeafFields(tree: RootField): Field[] {
  return getAllDescendants(tree).filter(
    (n) => n.type !== 'ROOT_FIELD' && n.type !== 'GROUP_FIELD',
  );
}

/**
 * True when a field's stored `value` is non-null AND its type matches the
 * field's current `type`. After a type change, a stale value of the wrong
 * shape is treated as unanswered (without being destroyed — switching the
 * type back restores the answer).
 */
export function hasValidAnswer(field: {
  type: string;
  value?: unknown;
}): boolean {
  if (!('value' in field) || field.value === null || field.value === undefined)
    return false;
  switch (field.type) {
    case 'CHECKPOINT_FIELD':
    case 'TASK_FIELD':
      return typeof field.value === 'boolean';
    case 'NUMERIC_FIELD':
      return typeof field.value === 'number';
    default:
      return false;
  }
}

/**
 * Calculate progress: { answered, total } for a built tree.
 * A field is "answered" when it has a valid type-matching value.
 */
export function calculateProgress(tree: RootField): {
  answered: number;
  total: number;
} {
  const leaves = getLeafFields(tree);
  const answered = leaves.filter(hasValidAnswer).length;
  return { answered, total: leaves.length };
}

/**
 * Immutably update a node in the tree identified by `targetNodeId`.
 * Walks from root to the target, producing new tree nodes along the path.
 * Returns the original list unchanged if the target node is not found.
 */
function patchTreeNode(
  tree: RootField,
  targetNodeId: UUID,
  modFunc: (node: Field) => Field,
): RootField {
  const path = findNodePath(tree, targetNodeId);
  if (!path) return tree;

  // Walk backwards from the target to the root, applying the modification
  // at the leaf and wrapping parents.
  let current: Field = modFunc(path[path.length - 1]);

  for (let i = path.length - 2; i >= 0; i--) {
    const parent = path[i];
    if (parent.type !== 'GROUP_FIELD' && parent.type !== 'ROOT_FIELD') {
      // This should not happen in a well-formed tree
      return tree;
    }
    const childIndex = parent.children.indexOf(path[i + 1]);
    const newChildren = [...parent.children];
    newChildren[childIndex] = current;
    current = { ...parent, children: newChildren };
  }

  return current as RootField;
}

function isStructureField(
  node: Field,
): node is RootField | (Field & StructureField) {
  return node.type === 'ROOT_FIELD' || node.type === 'GROUP_FIELD';
}

function isValueField(node: Field): node is ValueField & Field {
  return (
    node.type === 'CHECKPOINT_FIELD' ||
    node.type === 'NUMERIC_FIELD' ||
    node.type === 'TASK_FIELD'
  );
}

// ---------------------------------------------------------------------------
// Array insert helper
// ---------------------------------------------------------------------------

function insertAt<T>(arr: T[], index: number, item: T): T[] {
  const result = [...arr];
  if (index === -1 || index >= arr.length) {
    result.push(item);
  } else {
    result.splice(index, 0, item);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Individual operation handlers
// ---------------------------------------------------------------------------

function handleCreateList(list: BuiltList, event: CreateListEvent): BuiltList {
  const { payload } = event;
  return {
    ...list,
    id: event.list_id,
    creator_user_id: event.user_id,
    created: event.created,
    modified: event.created,
    status: 'open',
    ...(payload.name !== undefined && { name: payload.name }),
    ...(payload.werk_id !== undefined && { werk_id: payload.werk_id }),
    ...(payload.associated_users !== undefined && {
      associated_users: payload.associated_users,
    }),
    ...(payload.tags !== undefined && { tags: payload.tags }),
    ...(payload.start_date !== undefined && {
      start_date: payload.start_date,
    }),
    ...(payload.end_date !== undefined && {
      end_date: payload.end_date,
    }),
  };
}

function handleModifyList(list: BuiltList, event: ModifyListEvent): BuiltList {
  const { payload } = event;
  return {
    ...list,
    modified: event.created,
    ...(payload.name !== undefined && { name: payload.name }),
    ...(payload.werk_id !== undefined && { werk_id: payload.werk_id }),
    ...(payload.associated_users !== undefined && {
      associated_users: payload.associated_users,
    }),
    ...(payload.tags !== undefined && { tags: payload.tags }),
    ...(payload.start_date !== undefined && {
      start_date: payload.start_date,
    }),
    ...(payload.end_date !== undefined && {
      end_date: payload.end_date,
    }),
  };
}

function handleCloseList(
  list: BuiltList,
  event: ListEvent & { type: 'CLOSE_LIST' },
): BuiltList {
  return {
    ...list,
    status: 'closed',
    close_date: event.created,
    modified: event.created,
  };
}

function handleOpenList(
  list: BuiltList,
  event: ListEvent & { type: 'OPEN_LIST' },
): BuiltList {
  return {
    ...list,
    status: 'open',
    close_date: null,
    modified: event.created,
  };
}

function handleInsert(list: BuiltList, event: InsertEvent): BuiltList {
  const {
    payload: { index = -1, node, targetNodeId },
  } = event;

  const field: Field = {
    ...completePartialField(node, targetNodeId),
    originEventId: event.id,
  };

  const newTree = patchTreeNode(list.tree, targetNodeId, (parentNode) => {
    if (!isStructureField(parentNode)) {
      throw new Error('Expected insert target to be a structure-field');
    }
    return {
      ...parentNode,
      children: insertAt(parentNode.children, index, field),
    };
  });

  if (newTree === list.tree) return list;

  return { ...list, tree: newTree };
}

// NOTE: handlePatch applies partialNode fields via spread without validating
// that the fields are appropriate for the target node's type. For example,
// patching a `value: number` onto a CHECKPOINT_FIELD (which expects boolean)
// is not caught at the reducer level. This is consistent with the legacy
// implementation which also does not validate PATCH payloads against field
// types. INSERT validates completeness via completePartialField, but PATCH
// intentionally does not -- the Zod schema (zPatchField) provides the
// validation boundary instead.
function handlePatch(list: BuiltList, event: PatchEvent): BuiltList {
  const { partialNode, targetNodeId } = event.payload;

  const newTree = patchTreeNode(list.tree, targetNodeId, (node) => ({
    ...node,
    ...(partialNode as Record<string, unknown>),
    modifiedFields: unique([
      ...node.modifiedFields,
      ...Object.keys(partialNode),
    ]),
  }));

  if (newTree === list.tree) return list;

  return { ...list, tree: newTree };
}

function handleRemove(list: BuiltList, event: RemoveEvent): BuiltList {
  const { targetNodeId } = event.payload;

  const targetNode = findNode(list.tree, targetNodeId);
  if (!targetNode) return list;
  if (!targetNode.parentNodeId) {
    throw new Error('Unexpected target-node without parentNodeId (ROOT_FIELD)');
  }

  const newTree = patchTreeNode(
    list.tree,
    targetNode.parentNodeId,
    (parentNode) => {
      if (!isStructureField(parentNode)) {
        throw new Error(`Unexpected targetNode type (${parentNode.type})`);
      }
      return {
        ...parentNode,
        children: parentNode.children.filter((c) => c.id !== targetNodeId),
      };
    },
  );

  if (newTree === list.tree) return list;

  return { ...list, tree: newTree };
}

function handleMove(list: BuiltList, event: MoveEvent): BuiltList {
  const {
    payload: { index = -1, subjectNodeId, targetNodeId },
  } = event;

  const subjectNode = findNode(list.tree, subjectNodeId);
  if (!subjectNode) return list;
  if (!subjectNode.parentNodeId) {
    throw new Error('Unexpected move-subject without parentNodeId');
  }

  const newSubjectNode = { ...subjectNode, parentNodeId: targetNodeId };
  const sameParent = subjectNode.parentNodeId === targetNodeId;

  if (sameParent) {
    // Reorder within the same parent. The legacy implementation inserts first
    // (at the given index in the original array), then removes the original.
    // We replicate this as a single atomic operation on the children array.
    const newTree = patchTreeNode(list.tree, targetNodeId, (parentNode) => {
      if (!isStructureField(parentNode)) {
        throw new Error(`Unexpected targetNode type (${parentNode.type})`);
      }
      // Insert first (index is relative to the original array) ...
      const withInsert = insertAt(parentNode.children, index, newSubjectNode);
      // ... then remove the original (first occurrence with matching id
      // that is not the newly inserted reference).
      let removed = false;
      const result = withInsert.filter((c) => {
        if (!removed && c !== newSubjectNode && c.id === subjectNodeId) {
          removed = true;
          return false;
        }
        return true;
      });
      return { ...parentNode, children: result };
    });

    return { ...list, tree: newTree };
  }

  // Different parents: order doesn't matter. Insert first, then remove,
  // matching legacy behavior.

  // 1. Insert into new parent
  let newTree = patchTreeNode(list.tree, targetNodeId, (parentNode) => {
    if (!isStructureField(parentNode)) {
      throw new Error(`Unexpected targetNode type (${parentNode.type})`);
    }
    return {
      ...parentNode,
      children: insertAt(parentNode.children, index, newSubjectNode),
    };
  });

  const targetFound = newTree !== list.tree;

  // 2. Remove from old parent
  newTree = patchTreeNode(newTree, subjectNode.parentNodeId, (parentNode) => {
    if (!isStructureField(parentNode)) {
      throw new Error(`Unexpected targetNode type (${parentNode.type})`);
    }
    return {
      ...parentNode,
      children: parentNode.children.filter((c) => c.id !== subjectNodeId),
    };
  });

  if (!targetFound) {
    // Target parent did not exist: the subject was removed from its old
    // parent but not inserted anywhere. This mirrors the legacy behaviour
    // where the node is dropped from the index.
  }

  return { ...list, tree: newTree };
}

function handleCopy(list: BuiltList, event: CopyEvent): BuiltList {
  const {
    payload: { index = -1, subjectNodeId, targetNodeId },
  } = event;

  const subjectNode = findNode(list.tree, subjectNodeId);
  if (!subjectNode) return list;

  // Create deep copy with deterministic IDs
  const namespace = event.id;
  function copyWithNewIds(node: Field, parentNodeId: UUID): Field {
    const id = uuidV5(namespace, node.id);
    if (node.type === 'ROOT_FIELD') {
      throw new Error(
        `Unexpected node type in copy-subject children (${node.type})`,
      );
    }
    if (node.type === 'GROUP_FIELD') {
      const newNode: Field = {
        ...node,
        id,
        parentNodeId,
        originEventId: event.id,
        children: node.children.map((child) => copyWithNewIds(child, id)),
      };
      return newNode;
    }
    return { ...node, id, parentNodeId, originEventId: event.id };
  }

  const newNode = copyWithNewIds(subjectNode, targetNodeId);

  const newTree = patchTreeNode(list.tree, targetNodeId, (parentNode) => {
    if (!isStructureField(parentNode)) {
      throw new Error(`Unexpected targetNode type (${parentNode.type})`);
    }
    return {
      ...parentNode,
      children: insertAt(parentNode.children, index, newNode),
    };
  });

  if (newTree === list.tree) return list;

  return { ...list, tree: newTree };
}

function handleExpandProto(
  list: BuiltList,
  event: ExpandProtoEvent,
  dependencies: Record<string, BuiltList>,
): BuiltList {
  const {
    payload: { protoListId, targetNodeId },
  } = event;

  const proto = dependencies[protoListId];
  if (!proto) {
    throw new Error(
      `EXPAND_PROTO: proto list ${protoListId} not in dependencies. ` +
        `Caller must pre-resolve all dependencies before reducing events.`,
    );
  }

  const namespace = event.id;
  function newIds(node: Field, parentNodeId: UUID): Field {
    const id = uuidV5(namespace, node.id);
    if (node.type === 'ROOT_FIELD') {
      throw new Error('Unexpected proto field; cant insert RootField');
    }

    const newNode: Field = {
      ...node,
      protoChain: [
        { listId: protoListId, nodeId: node.id },
        ...node.protoChain,
      ],
      modifiedFields: [],
      originEventId: event.id,
      id,
      parentNodeId,
    } as Field;

    if (newNode.type === 'GROUP_FIELD') {
      return {
        ...newNode,
        children: newNode.children.map((child) => newIds(child, id)),
      };
    }

    return newNode;
  }

  if (proto.tree.type !== 'ROOT_FIELD') {
    throw new Error('Expected proto to be a root-field');
  }
  const protoFields = proto.tree.children.map((f) => newIds(f, targetNodeId));

  const newTree = patchTreeNode(list.tree, targetNodeId, (targetNode) => {
    if (!isStructureField(targetNode)) {
      throw new Error('Expected expandProto target to be a structure-field');
    }
    return {
      ...targetNode,
      children: [...targetNode.children, ...protoFields],
    };
  });

  if (newTree === list.tree) return list;

  return { ...list, tree: newTree };
}

// ---------------------------------------------------------------------------
// Attachment / Note / Geotag handlers
// ---------------------------------------------------------------------------

function handleAddAttachment(
  list: BuiltList,
  event: AddAttachmentEvent,
): BuiltList {
  const newTree = patchTreeNode(
    list.tree,
    event.payload.targetNodeId,
    (targetNode) => {
      if (!isValueField(targetNode)) {
        throw new Error(
          `Unexpected non-value targetNode type (${targetNode.type}).`,
        );
      }
      const fileAttachment: FileAttachment = {
        type: 'attachment',
        ...event.payload.attachment,
        id: event.id,
        user_id: event.user_id,
        created: event.created,
      };
      return {
        ...targetNode,
        attachments: [...targetNode.attachments, fileAttachment],
      };
    },
  );

  if (newTree === list.tree) return list;
  return { ...list, tree: newTree };
}

function handleRemoveAttachment(
  list: BuiltList,
  event: RemoveAttachmentEvent,
): BuiltList {
  const newTree = patchTreeNode(
    list.tree,
    event.payload.targetNodeId,
    (targetNode) => {
      if (!isValueField(targetNode)) {
        throw new Error(
          `Unexpected non-value targetNode type (${targetNode.type}).`,
        );
      }
      return {
        ...targetNode,
        attachments: targetNode.attachments.filter(
          (att) =>
            !(
              att.type === 'attachment' &&
              att.checksum === event.payload.checksum
            ),
        ),
      };
    },
  );

  if (newTree === list.tree) return list;
  return { ...list, tree: newTree };
}

function handleAddNote(list: BuiltList, event: AddNoteEvent): BuiltList {
  const newTree = patchTreeNode(
    list.tree,
    event.payload.targetNodeId,
    (targetNode) => {
      if (!isValueField(targetNode)) {
        throw new Error(
          `Unexpected non-value targetNode type (${targetNode.type}).`,
        );
      }
      const noteAttachment: NoteAttachment = {
        type: 'note',
        text: event.payload.note,
        id: event.id,
        user_id: event.user_id,
        created: event.created,
      };
      return {
        ...targetNode,
        attachments: [...targetNode.attachments, noteAttachment],
      };
    },
  );

  if (newTree === list.tree) return list;
  return { ...list, tree: newTree };
}

function handleEditNote(list: BuiltList, event: EditNoteEvent): BuiltList {
  const newTree = patchTreeNode(
    list.tree,
    event.payload.targetNodeId,
    (targetNode) => {
      if (!isValueField(targetNode)) {
        throw new Error(
          `Unexpected non-value targetNode type (${targetNode.type}).`,
        );
      }
      return {
        ...targetNode,
        attachments: targetNode.attachments.map((att) => {
          if (att.id === event.payload.noteId && att.type === 'note') {
            return { ...att, text: event.payload.text };
          }
          return att;
        }),
      };
    },
  );

  if (newTree === list.tree) return list;
  return { ...list, tree: newTree };
}

function handleRemoveNote(list: BuiltList, event: RemoveNoteEvent): BuiltList {
  const newTree = patchTreeNode(
    list.tree,
    event.payload.targetNodeId,
    (targetNode) => {
      if (!isValueField(targetNode)) {
        throw new Error(
          `Unexpected non-value targetNode type (${targetNode.type}).`,
        );
      }
      return {
        ...targetNode,
        attachments: targetNode.attachments.filter(
          (att) => att.id !== event.payload.noteId,
        ),
      };
    },
  );

  if (newTree === list.tree) return list;
  return { ...list, tree: newTree };
}

function handleAddGeotag(list: BuiltList, event: AddGeotagEvent): BuiltList {
  const newTree = patchTreeNode(
    list.tree,
    event.payload.targetNodeId,
    (targetNode) => {
      if (!isValueField(targetNode)) {
        throw new Error(
          `Unexpected non-value targetNode type (${targetNode.type}).`,
        );
      }
      const geotagAttachment: GeotagAttachment = {
        type: 'geotag',
        geotag: event.payload.geotag,
        id: event.id,
        user_id: event.user_id,
        created: event.created,
      };
      return {
        ...targetNode,
        attachments: [...targetNode.attachments, geotagAttachment],
      };
    },
  );

  if (newTree === list.tree) return list;
  return { ...list, tree: newTree };
}

function handleEditGeotag(list: BuiltList, event: EditGeotagEvent): BuiltList {
  const newTree = patchTreeNode(
    list.tree,
    event.payload.targetNodeId,
    (targetNode) => {
      if (!isValueField(targetNode)) {
        throw new Error(
          `Unexpected non-value targetNode type (${targetNode.type}).`,
        );
      }
      return {
        ...targetNode,
        attachments: targetNode.attachments.map((att) => {
          if (att.id === event.payload.geotagId && att.type === 'geotag') {
            return {
              ...att,
              geotag: { ...att.geotag, ...event.payload.geotag },
            };
          }
          return att;
        }),
      };
    },
  );

  if (newTree === list.tree) return list;
  return { ...list, tree: newTree };
}

function handleRemoveGeotag(
  list: BuiltList,
  event: RemoveGeotagEvent,
): BuiltList {
  const newTree = patchTreeNode(
    list.tree,
    event.payload.targetNodeId,
    (targetNode) => {
      if (!isValueField(targetNode)) {
        throw new Error(
          `Unexpected non-value targetNode type (${targetNode.type}).`,
        );
      }
      return {
        ...targetNode,
        attachments: targetNode.attachments.filter(
          (att) => att.id !== event.payload.geotagId,
        ),
      };
    },
  );

  if (newTree === list.tree) return list;
  return { ...list, tree: newTree };
}

// ---------------------------------------------------------------------------
// Main reducer
// ---------------------------------------------------------------------------

/**
 * Reduces a single list event against the current built list state.
 * Dependencies must be provided externally for EXPAND_PROTO events.
 */
export function listEventReducer(
  list: BuiltList,
  event: ListEvent,
  dependencies: Record<string, BuiltList> = {},
): BuiltList {
  switch (event.type) {
    case 'CREATE_LIST':
      return handleCreateList(list, event);
    case 'MODIFY_LIST':
      return handleModifyList(list, event);
    case 'CLOSE_LIST':
      return handleCloseList(list, event);
    case 'OPEN_LIST':
      return handleOpenList(list, event);
    case 'INSERT':
      return handleInsert(list, event);
    case 'PATCH':
      return handlePatch(list, event);
    case 'REMOVE':
      return handleRemove(list, event);
    case 'MOVE':
      return handleMove(list, event);
    case 'COPY':
      return handleCopy(list, event);
    case 'EXPAND_PROTO':
      return handleExpandProto(list, event, dependencies);
    case 'ADD_ATTACHMENT':
      return handleAddAttachment(list, event);
    case 'REMOVE_ATTACHEMNT':
      return handleRemoveAttachment(list, event);
    case 'ADD_NOTE':
      return handleAddNote(list, event);
    case 'EDIT_NOTE':
      return handleEditNote(list, event);
    case 'REMOVE_NOTE':
      return handleRemoveNote(list, event);
    case 'ADD_GEOTAG':
      return handleAddGeotag(list, event);
    case 'EDIT_GEOTAG':
      return handleEditGeotag(list, event);
    case 'REMOVE_GEOTAG':
      return handleRemoveGeotag(list, event);
    default:
      assertNever(event);
  }
}

// ---------------------------------------------------------------------------
// Initialize empty BuiltList
// ---------------------------------------------------------------------------

export function initializeBuiltList(listId: UUID): BuiltList {
  const tree = createRootField({ id: listId });
  return {
    id: listId,
    name: '',
    associated_users: [],
    tags: [],
    werk_id: null,
    creator_user_id: 0,
    status: 'open',
    close_date: null,
    start_date: null,
    end_date: null,
    created: new Date(0),
    modified: new Date(0),
    deleted: false,
    dependencies: [],
    dependants: [],
    tree,
  };
}

// ---------------------------------------------------------------------------
// mergeSimilarModifications
// ---------------------------------------------------------------------------

/**
 * Merge consecutive PATCH events that target the same node by the same user
 * within 60 seconds of each other. Reduces event count for keystroke-heavy
 * editing sessions.
 */
export function mergeSimilarModifications(
  events: ListEvent[],
  since: string | null = null,
): ListEvent[] {
  const result: ListEvent[] = [];
  let sincePassed = !since;
  let pendingPatch: PatchEvent | null = null;

  for (const event of events) {
    if (sincePassed) {
      if (pendingPatch) {
        if (
          event.type === 'PATCH' &&
          pendingPatch.user_id === event.user_id &&
          pendingPatch.payload.targetNodeId === event.payload.targetNodeId &&
          event.created.getTime() - pendingPatch.created.getTime() < 60_000
        ) {
          // Merge: keep the first event's id (may already be referenced),
          // use the latest event's timestamp, merge partialNodes.
          pendingPatch = {
            ...event,
            id: pendingPatch.id,
            payload: {
              ...event.payload,
              partialNode: {
                ...(pendingPatch.payload.partialNode as Record<
                  string,
                  unknown
                >),
                ...(event.payload.partialNode as Record<string, unknown>),
              },
            },
          };
          continue;
        }
      }
    }

    // Pending patch from previous iteration did not match -> push
    if (pendingPatch) {
      result.push(pendingPatch);
      pendingPatch = null;
    }

    if (event.type === 'PATCH' && sincePassed) {
      pendingPatch = event;
    } else {
      result.push(event);
    }

    sincePassed = sincePassed || event.id === since;
  }

  if (since && !sincePassed) {
    throw new Error(
      `"since" (${since}) was specified but not found in the provided modifications`,
    );
  }

  if (pendingPatch) {
    result.push(pendingPatch);
  }

  return result;
}
